import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import axiosInstance from "../../utils/axiosInstance";

const AirbnbBadges = ({ airbnbId }) => {
  const [allBadges, setAllBadges] = useState([]);
  const [assignedBadges, setAssignedBadges] = useState([]);
  const [selectedBadge, setSelectedBadge] = useState("");

  // Fetch all badges
  const fetchAllBadges = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/airbnbs/badges");
      setAllBadges(res.data);
    } catch (err) {
      console.error("❌ Failed to fetch badges:", err);
    }
  }, []);

  // Fetch assigned badges for this Airbnb
  const fetchAssignedBadges = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/airbnbs/${airbnbId}/badges`);
      setAssignedBadges(res.data || []);
    } catch (err) {
      console.error("❌ Failed to fetch assigned badges:", err);
    }
  }, [airbnbId]);

  useEffect(() => {
    fetchAllBadges();
    fetchAssignedBadges();
  }, [fetchAllBadges, fetchAssignedBadges]);

  // Assign new badge
  const handleAssignBadge = async () => {
    if (!selectedBadge) return;
    try {
      await axiosInstance.post(`/airbnbs/${airbnbId}/badges`, {
        badge_id: Number(selectedBadge),
      });
      setSelectedBadge("");
      fetchAssignedBadges();
    } catch (err) {
      console.error("❌ Failed to assign badge:", err);
    }
  };

  // Remove badge
  const handleRemoveBadge = async (badgeId) => {
    if (window.confirm("Remove this badge?")) {
      try {
        await axiosInstance.delete(`/airbnbs/${airbnbId}/badges/${badgeId}`);
        fetchAssignedBadges();
      } catch (err) {
        console.error("❌ Failed to remove badge:", err);
      }
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6">Manage Badges</Typography>

      {/* Assign New Badge */}
      <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
        <Select
          value={selectedBadge}
          onChange={(e) => setSelectedBadge(e.target.value)}
          displayEmpty
          fullWidth
        >
          <MenuItem value="">Select Badge</MenuItem>
          {allBadges.map((b) => {
            const isAssigned = assignedBadges.some((ab) => Number(ab.id) === Number(b.id));
            return (
              <MenuItem key={b.id} value={b.id} disabled={isAssigned}>
                {b.name} {isAssigned ? "(assigned)" : ""}
              </MenuItem>
            );
          })}
        </Select>
        <Button variant="contained" onClick={handleAssignBadge}>
          Assign
        </Button>
      </Box>

      {/* List Assigned Badges */}
      <Box sx={{ mt: 2 }}>
        {assignedBadges.length > 0 ? (
          assignedBadges.map((b) => (
            <Box
              key={b.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 1.5,
                mb: 1,
                border: "1px solid #ccc",
                borderRadius: 2,
              }}
            >
              <Typography>{b.name}</Typography>
              <IconButton onClick={() => handleRemoveBadge(b.id)}>
                <DeleteIcon color="error" />
              </IconButton>
            </Box>
          ))
        ) : (
          <Typography>No badges assigned.</Typography>
        )}
      </Box>
    </Box>
  );
};

export default AirbnbBadges;
